import { User } from '@/api';
import useAuth from '@/contexts/AuthContext';
import useUnAuth from '@/contexts/UnAuthContext';
import { useSearchParams, useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import OtpForm from './OtpForm';

export function useOnAccountLogin(isMobile?: boolean) {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { setUser } = useAuth();
    // On desktop the post-auth target is passed via context (not the URL) — e.g.
    // "Sign up to bid" opens the modal with a `pa` intent redirect. Honor it so
    // logging in (not just signing up) resumes the intent.
    const { authRedirect } = useUnAuth();
    const [showOtp, setShowOtp] = useState<boolean>(false);
    const [fourDigits, setFourDigits] = useState<string>('');

    const redirectAfterLogin = useCallback(() => {
        if (searchParams.get('redirect')) {
            router.push(searchParams.get('redirect') || '/');
        } else if (authRedirect) {
            router.push(authRedirect);
        } else if (isMobile) {
            router.push('/');
        }
    }, [authRedirect, isMobile, router, searchParams]);

    const onAccountLogin = (user: User) => {
        if (user.is_two_factor_enabled) {
            setShowOtp(true);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            setFourDigits(user.two_factor_phone_number_last4!);
        } else {
            setUser(user);
            redirectAfterLogin();
        }
    };

    const otpForm = useMemo(() => {
        if (showOtp) {
            return <OtpForm onLoginSuccess={redirectAfterLogin} fourDigits={fourDigits ?? ''} />;
        }
        return null;
    }, [fourDigits, redirectAfterLogin, showOtp]);

    return { onAccountLogin, showOtp, otpForm };
}
